import { useQuery, useQueries } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaEmailRecordsV1Service } from '../service';
import { GetLocationPersonalizationIO } from '../types';

export const getGetLocationPersonalizationQueryKey = (req: GetLocationPersonalizationIO['input']) =>
  ['EmailRecordsV1', 'GetLocationPersonalization', req.locationId] as const;
type QueryKey = ReturnType<typeof getGetLocationPersonalizationQueryKey>;

/**
 * A hook that returns a query for the `GetLocationPersonalization` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetLocationPersonalizationQuery = <E = unknown, D = GetLocationPersonalizationIO['output']>(
  req: GetLocationPersonalizationIO['input'],
  options?: LimitedSchemaQueryOptions<GetLocationPersonalizationIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetLocationPersonalizationIO['output'], E, D, QueryKey>({
    queryKey: getGetLocationPersonalizationQueryKey(req),
    queryFn: () => SchemaEmailRecordsV1Service.GetLocationPersonalization(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetLocationPersonalization` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetLocationPersonalizationQueries = <E = unknown, D = GetLocationPersonalizationIO['output']>(
  argsArr: {
    req: GetLocationPersonalizationIO['input'];
    options?: LimitedSchemaQueryOptions<GetLocationPersonalizationIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetLocationPersonalizationQueryKey(req),
      queryFn: () => SchemaEmailRecordsV1Service.GetLocationPersonalization(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetLocationPersonalizationQuery<E, D>>[];
